import { CONSUMER, TRADE } from 'dibs-buyer-layout/exports/userTypeEnums.ts';
import {
    BILLBOARD_AD_KEY,
    HOLIDAY_COLLECTION_SEGMENT,
    SEARCH_SEGMENT,
    TILE_AD_KEY,
} from '../constants.ts';
import {
    type AdKey,
    type UserType,
    type AdData,
    type AdProductTile,
    type AdSkyscraper,
    type AdBillboard,
    type AdDeviceType,
} from '../types.ts';
import { sbAdExcludeMap, googleAdMap, tradeGoogleAdMap } from './googleAdMap.ts';

const getUriRefSegments = (uriRef: string): string[] =>
    (uriRef || '')
        .split('?')[0]
        .split('/')
        .filter(segment => segment !== '');

export const getAdData = ({
    uriRef,
    adKey,
    userType,
}: {
    uriRef: string;
    adKey: AdKey;
    userType: UserType;
}): AdData | null => {
    let segments = getUriRefSegments(uriRef);
    const lastSegment = segments[segments.length - 1];
    const isSearch = segments.includes(SEARCH_SEGMENT);

    // remove search segment to get vertical targeted ads
    // e.g. for semantic and classified search pages
    if (isSearch) {
        segments = segments.filter(segment => segment !== SEARCH_SEGMENT);
    }

    // holiday collection tile ads should show only when no filters applied
    if (
        adKey === TILE_AD_KEY &&
        segments.includes(HOLIDAY_COLLECTION_SEGMENT) &&
        !lastSegment.includes(HOLIDAY_COLLECTION_SEGMENT)
    ) {
        segments = segments.filter(segment => segment !== HOLIDAY_COLLECTION_SEGMENT);
    }

    // run exclusion check: if a segment is present in the exclusion map, check the
    // adKey.  if they both match, return null
    if (
        segments.find(segment => sbAdExcludeMap[segment] && sbAdExcludeMap[segment].includes(adKey))
    ) {
        return null;
    }

    // if trade, use the trade segments before the consumer segments
    const key =
        (userType === TRADE ? segments.find(segment => tradeGoogleAdMap[segment]) : '') ||
        segments.find(segment => googleAdMap[segment]) ||
        'default';

    if (userType === TRADE && tradeGoogleAdMap[key]) {
        return tradeGoogleAdMap[key];
    }

    // For search pages where no vertical targeted ads are available, show search ads
    return isSearch && key === 'default' ? googleAdMap[SEARCH_SEGMENT] : googleAdMap[key];
};

export function getProductTileAdData({
    uriRef,
    userType = CONSUMER,
    deviceType,
}: {
    uriRef: string;
    userType?: UserType;
    deviceType: AdDeviceType;
}): AdProductTile[] {
    const adData = getAdData({ uriRef, userType, adKey: TILE_AD_KEY });
    const ad = adData?.[TILE_AD_KEY];
    if (ad) {
        if (Array.isArray(ad)) {
            return ad;
        }
        return ad[deviceType] || [];
    }
    return [];
}

export function getBillboardAdData(
    uriRef: string,
    userType: UserType = CONSUMER
): AdBillboard | null {
    const ad = getAdData({ uriRef, userType, adKey: BILLBOARD_AD_KEY });
    return ad?.[BILLBOARD_AD_KEY] || null;
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export function getSkyscraperAdData(uriRef: string): AdSkyscraper | null {
    // we don't use this any more, but there's still some code present
    // this allows for some cleanup
    return null;
}
